import type { JSX, ReactNode } from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl';

import { FontSize, FontSizes, FontWeight, FontWeights, PaletteColor } from '@calm-web/design-system';

import { useAuthPrompt, type AuthPrompt } from '@/context/auth/AuthPromptProvider';
import { usePricesState } from '@/hooks/store';
import type { Prices } from '@/store/prices/types';

import {
	BulletPoint,
	CheckIcon,
	CircleIcon,
	List,
	SubTitle,
	Title,
	Unordered,
	ValuePropBody,
	ValuePropHeader,
	ValuePropTextContainer,
} from './styles';

export type ValuePropsListProps = {
	title?: ReactNode | ReactNode[];
	subTitle?: ReactNode | ReactNode[];
	copy?: Array<CopyItem>;
	buttonColor: PaletteColor;
	textColor?: PaletteColor;
	fontWeight?: FontWeight;
	headerSize?: FontSize;
	propSize?: FontSize;
	propPadding?: boolean;
	gapSize?: string;
	titleSize?: FontSize;
	titleColor?: PaletteColor;
	className?: string;
	isRevampRedempFlow?: boolean;
	isB2BAccountPage?: boolean;
	isOrdered?: boolean;
	isForMobile?: boolean;
};

export type CopyItem = {
	header?:
		| MessageDescriptor
		| (({ authFormMode, prices }: { authFormMode: AuthPrompt; prices: Prices }) =>
				| {
						message: MessageDescriptor;
						placeholders?: { [key: string]: string | number | boolean | (() => JSX.Element) };
				  }
				| undefined);
	body?:
		| MessageDescriptor
		| (({ authFormMode, prices }: { authFormMode: AuthPrompt; prices: Prices }) =>
				| {
						message: MessageDescriptor;
						placeholders?: { [key: string]: string | number | boolean | (() => JSX.Element) };
				  }
				| undefined);
	id: string;
};

const ValuePropsList = ({
	buttonColor,
	fontWeight,
	textColor = 'black',
	title,
	subTitle,
	copy,
	headerSize = FontSizes.base,
	propSize = FontSizes.base,
	propPadding = false,
	gapSize,
	titleSize,
	titleColor,
	className,
	isRevampRedempFlow,
	isB2BAccountPage,
	isOrdered,
	isForMobile,
}: ValuePropsListProps) => {
	const { formatMessage } = useIntl();
	const { authPrompt } = useAuthPrompt();
	const prices = usePricesState();

	const getBulletIcon = (index: number) => {
		if (isOrdered) {
			return (
				<CircleIcon aria-hidden $color={buttonColor}>
					{index + 1}
				</CircleIcon>
			);
		}

		return <CheckIcon aria-hidden $color={buttonColor} />;
	};

	const getItemHeader = (item: CopyItem) => {
		let formattedMessage;
		if (!item.header) return null;
		if (typeof item.header === 'function') {
			const itemHeader = item.header({ authFormMode: authPrompt, prices });
			if (!itemHeader) return null;
			const { message, placeholders = {} } = itemHeader;
			formattedMessage = formatMessage(message, placeholders);
		} else {
			formattedMessage = formatMessage(item.header);
		}
		return (
			<ValuePropHeader
				el="p"
				size={headerSize}
				weight={FontWeights.Demi}
				data-testid={`value-prop-list-item-header${isForMobile ? '-mobile' : ''}`}
			>
				{formattedMessage}
			</ValuePropHeader>
		);
	};

	const getItemBody = (item: CopyItem) => {
		let formattedMessage;
		if (!item.body) return null;
		if (typeof item.body === 'function') {
			const itemBody = item.body({ authFormMode: authPrompt, prices });
			if (!itemBody) return null;
			const { message, placeholders = {} } = itemBody;
			formattedMessage = formatMessage(message, placeholders);
		} else {
			formattedMessage = formatMessage(item.body);
		}
		return (
			<ValuePropBody
				el="p"
				size={propSize}
				data-testid={`value-prop-list-item-body${isForMobile ? '-mobile' : ''}`}
			>
				{formattedMessage}
			</ValuePropBody>
		);
	};

	return (
		<List hasTitle={!!title} className={className} isRevampRedempFlow={isRevampRedempFlow}>
			{title && (
				<Title
					el={isB2BAccountPage ? 'h1' : 'h3'}
					color={titleColor || textColor}
					size={titleSize || FontSizes.lg}
					weight={fontWeight || FontWeights.Regular}
					align="left"
					isRevampRedempFlow={isRevampRedempFlow}
					data-testid={`value-prop-list-title${isForMobile ? '-mobile' : ''}`}
				>
					{title}
				</Title>
			)}
			{subTitle && <SubTitle>{subTitle}</SubTitle>}
			{copy && (
				<Unordered gapSize={gapSize} isB2BAccountPage={isB2BAccountPage}>
					{copy.map((item, index) => (
						<BulletPoint key={item.id} $textColor={textColor}>
							{getBulletIcon(index)}
							<ValuePropTextContainer propPadding={propPadding}>
								{getItemHeader(item)}
								{getItemBody(item)}
							</ValuePropTextContainer>
						</BulletPoint>
					))}
				</Unordered>
			)}
		</List>
	);
};

export default ValuePropsList;
